/**
 * Shell UI for the interactive canvas: dialogs, menus, grid toggle, quick-add,
 * toolbar popover coordination (state + setter for Radix / `useCanvasOnNodesChange`).
 * Thumbnail capture iframe uses `variant="capture"` (noop toolbar + frozen defaults).
 */

import {
  CANVAS_CONTROLS_STORAGE_KEYS,
  parseCanvasCursorTool,
  parseCanvasGridMode,
  type CanvasCursorTool,
  type CanvasGridMode,
} from "@/pages/canvas/canvas-controls.types";
import type { ConnectorPreset } from "@/pages/canvas/hooks/useConnectorPlacement";
import { useLocalStorageState } from "ahooks";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from "react";

export interface QuickAddCursorState {
  screenX: number;
  screenY: number;
  clientX: number;
  clientY: number;
  flowPosition: { x: number; y: number };
}

export interface EdgeContextMenuState {
  edgeId: string;
  screenX: number;
  screenY: number;
}

export interface NodeContextMenuState {
  nodeId: string;
  nodeType: string | undefined;
  screenX: number;
  screenY: number;
  clientX: number;
  clientY: number;
  flowPosition: { x: number; y: number };
  /**
   * Where the menu was opened from. "pointer" means `flowPosition` is the
   * point the user aimed at; "anchor" means it is a toolbar button's edge,
   * which actions that place something on the board must not read as intent.
   */
  origin: "pointer" | "anchor";
}

/** Which HTML block the user is inside, and in which mode. */
export interface HtmlBlockFocus {
  nodeId: string;
  mode: "interact" | "edit";
}

export type CanvasUiVariant = "interactive" | "capture" | "view";

export interface CanvasUiContextType {
  /** Which surface this canvas is: the editor, the published view, or the
   *  offscreen screenshot render. The capture has one shot at the whole
   *  canvas, so it paces block documents on its own terms. */
  variant: CanvasUiVariant;
  /** Derived: true when any grid variant is visible. */
  showGrid: boolean;
  gridMode: CanvasGridMode;
  setGridMode: Dispatch<SetStateAction<CanvasGridMode>>;
  cursorTool: CanvasCursorTool;
  setCursorTool: Dispatch<SetStateAction<CanvasCursorTool>>;
  /** Alignment snapping + helper lines while dragging (lattice ruling A5). */
  alignmentGuides: boolean;
  setAlignmentGuides: Dispatch<SetStateAction<boolean>>;
  isWidgetsMenuOpen: boolean;
  setIsWidgetsMenuOpen: Dispatch<SetStateAction<boolean>>;
  isLinkMenuOpen: boolean;
  setIsLinkMenuOpen: Dispatch<SetStateAction<boolean>>;
  showQuickAdd: boolean;
  setShowQuickAdd: Dispatch<SetStateAction<boolean>>;
  quickAddCursor: QuickAddCursorState | null;
  setQuickAddCursor: Dispatch<SetStateAction<QuickAddCursorState | null>>;
  nodeContextMenu: NodeContextMenuState | null;
  setNodeContextMenu: Dispatch<SetStateAction<NodeContextMenuState | null>>;
  edgeContextMenu: EdgeContextMenuState | null;
  setEdgeContextMenu: Dispatch<SetStateAction<EdgeContextMenuState | null>>;
  isShapesMenuOpen: boolean;
  setIsShapesMenuOpen: Dispatch<SetStateAction<boolean>>;
  activeConnectorPreset: ConnectorPreset | null;
  setActiveConnectorPreset: Dispatch<SetStateAction<ConnectorPreset | null>>;
  isHoveringControls: boolean;
  setIsHoveringControls: Dispatch<SetStateAction<boolean>>;
  /**
   * True while any canvas toolbar popover (node toolbars, edge toolbar, etc.) is open.
   * Popovers call `setToolbarPopoverOpen` on open/close so `useCanvasOnNodesChange` can
   * filter stray `select: false` from Radix portal dismissal.
   */
  isToolbarPopoverOpen: boolean;
  setToolbarPopoverOpen: Dispatch<SetStateAction<boolean>>;
  /**
   * True while the primary toolbar's tray is popped. A detached
   * tray survives board interaction (D22), so a pane click leaves the menu
   * flags that back it alone while this is set (D31).
   */
  isToolbarTrayDetached: boolean;
  setToolbarTrayDetached: Dispatch<SetStateAction<boolean>>;
  /** Edge currently being label-edited inline (double-click / toolbar). null = none. */
  editingEdgeId: string | null;
  setEditingEdgeId: Dispatch<SetStateAction<string | null>>;
  /**
   * The HTML block the user is currently "inside", and how.
   *
   * `interact` — activated by double-click (Figma-embed model): the block's
   * iframe owns the pointer, so clicks and inline JS reach the content.
   * `edit` — the toolbar pencil: agent-marked islands become editable.
   *
   * One latch for both modes, so there is exactly one place that decides who
   * holds the pointer and exactly one falling edge to flush pending edits on.
   * Transient and per-user; entering on one block releases any other.
   */
  blockFocus: HtmlBlockFocus | null;
  setBlockFocus: Dispatch<SetStateAction<HtmlBlockFocus | null>>;
  /** Outer React Flow wrapper div; assigned by `CanvasPageInner` ref callback. */
  reactFlowWrapperRef: RefObject<HTMLDivElement | null>;
  /** Same element as `reactFlowWrapperRef.current`; state so layout hooks can subscribe. */
  reactFlowWrapperEl: HTMLDivElement | null;
  setReactFlowWrapperEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
  isResizingRef: RefObject<boolean>;
}

const CanvasUiContext = createContext<CanvasUiContextType | null>(null);

const noopDispatchBool: Dispatch<SetStateAction<boolean>> = () => {};
const noopDispatchQuickAdd: Dispatch<
  SetStateAction<QuickAddCursorState | null>
> = () => {};
const noopDispatchNodeCtx: Dispatch<
  SetStateAction<NodeContextMenuState | null>
> = () => {};
const noopDispatchEdgeCtx: Dispatch<
  SetStateAction<EdgeContextMenuState | null>
> = () => {};
const noopDispatchConnector: Dispatch<
  SetStateAction<ConnectorPreset | null>
> = () => {};
const noopDispatchGridMode: Dispatch<SetStateAction<CanvasGridMode>> = () => {};
const noopDispatchCursorTool: Dispatch<
  SetStateAction<CanvasCursorTool>
> = () => {};
const noopDispatchHover: Dispatch<SetStateAction<boolean>> = () => {};
const noopSetReactFlowWrapperEl: Dispatch<
  SetStateAction<HTMLDivElement | null>
> = () => {};

const CAPTURE_REACT_FLOW_WRAPPER_REF: RefObject<HTMLDivElement | null> = {
  current: null,
};

/** Static value for thumbnail capture iframe (shared node types call `useCanvasUi`). */
const CANVAS_UI_CAPTURE_VALUE: CanvasUiContextType = {
  variant: "capture",
  showGrid: true,
  gridMode: "lines",
  setGridMode: noopDispatchGridMode,
  cursorTool: "arrow",
  setCursorTool: noopDispatchCursorTool,
  alignmentGuides: false,
  setAlignmentGuides: noopDispatchBool,
  isWidgetsMenuOpen: false,
  setIsWidgetsMenuOpen: noopDispatchBool,
  isLinkMenuOpen: false,
  setIsLinkMenuOpen: noopDispatchBool,
  showQuickAdd: false,
  setShowQuickAdd: noopDispatchBool,
  quickAddCursor: null,
  setQuickAddCursor: noopDispatchQuickAdd,
  nodeContextMenu: null,
  setNodeContextMenu: noopDispatchNodeCtx,
  edgeContextMenu: null,
  setEdgeContextMenu: noopDispatchEdgeCtx,
  isShapesMenuOpen: false,
  setIsShapesMenuOpen: noopDispatchBool,
  activeConnectorPreset: null,
  setActiveConnectorPreset: noopDispatchConnector,
  isHoveringControls: false,
  setIsHoveringControls: noopDispatchHover,
  isToolbarPopoverOpen: false,
  setToolbarPopoverOpen: noopDispatchBool,
  isToolbarTrayDetached: false,
  setToolbarTrayDetached: noopDispatchBool,
  editingEdgeId: null,
  setEditingEdgeId: () => {},
  blockFocus: null,
  setBlockFocus: () => {},
  reactFlowWrapperRef: CAPTURE_REACT_FLOW_WRAPPER_REF,
  reactFlowWrapperEl: null,
  setReactFlowWrapperEl: noopSetReactFlowWrapperEl,
  isResizingRef: { current: false },
};

export function useCanvasUi(): CanvasUiContextType {
  const ctx = useContext(CanvasUiContext);
  if (!ctx) {
    throw new Error("useCanvasUi must be used within CanvasUiProvider");
  }
  return ctx;
}

export function CanvasUiProvider({
  children,
  variant = "interactive",
}: {
  children: ReactNode;
  variant?: CanvasUiVariant;
}) {
  if (variant === "capture") {
    return (
      <CanvasUiContext.Provider value={CANVAS_UI_CAPTURE_VALUE}>
        {children}
      </CanvasUiContext.Provider>
    );
  }

  // The published view starts every open at the defaults (grid on) and keeps
  // the picker's changes for the session only — a link should look the same
  // to everyone who opens it, not follow the viewer's app preference.
  return (
    <CanvasUiInteractiveProvider
      variant={variant}
      persistControls={variant !== "view"}
    >
      {children}
    </CanvasUiInteractiveProvider>
  );
}

/**
 * A control preference: persisted (and shared with the Manage modal's
 * preference rows) on the interactive canvas, session-only on the published
 * view. Both stores are read every render so hook order never changes.
 */
function useCanvasControlSetting<T>(
  key: string,
  defaultValue: T,
  persist: boolean,
): [T, (value: T | ((prev: T | undefined) => T)) => void] {
  const [stored, setStored] = useLocalStorageState<T>(key, {
    defaultValue,
    listenStorageChange: persist,
  });
  const [session, setSession] = useState<T>(defaultValue);
  return persist ? [stored, setStored] : [session, setSession];
}

function CanvasUiInteractiveProvider({
  children,
  variant,
  persistControls = true,
}: {
  children: ReactNode;
  variant: CanvasUiVariant;
  persistControls?: boolean;
}) {
  // `listenStorageChange` so a canvas open behind the Manage modal follows
  // the grid preference the moment it is changed there (this provider only
  // mounts on canvas pages; the preference row writes the same key).
  const [gridModeRaw, setGridModeRaw] = useCanvasControlSetting<CanvasGridMode>(
    CANVAS_CONTROLS_STORAGE_KEYS.gridMode,
    "lines",
    persistControls,
  );
  const gridMode = parseCanvasGridMode(gridModeRaw);
  const [cursorToolRaw, setCursorToolRaw] =
    useCanvasControlSetting<CanvasCursorTool>(
      CANVAS_CONTROLS_STORAGE_KEYS.cursorTool,
      "arrow",
      persistControls,
    );
  const cursorTool = parseCanvasCursorTool(cursorToolRaw);
  const showGrid = gridMode !== "none";

  const [alignmentGuidesRaw, setAlignmentGuidesRaw] =
    useCanvasControlSetting<boolean>(
      CANVAS_CONTROLS_STORAGE_KEYS.alignmentGuides,
      true,
      persistControls,
    );
  const alignmentGuides = alignmentGuidesRaw !== false;
  const setAlignmentGuides = useCallback<Dispatch<SetStateAction<boolean>>>(
    (value) => {
      setAlignmentGuidesRaw((prev) => {
        if (typeof value === "function") {
          return value(prev !== false);
        }
        return value;
      });
    },
    [setAlignmentGuidesRaw],
  );

  const setGridMode = useCallback<Dispatch<SetStateAction<CanvasGridMode>>>(
    (value) => {
      setGridModeRaw((prev) => {
        if (typeof value === "function") {
          return value(parseCanvasGridMode(prev));
        }
        return value;
      });
    },
    [setGridModeRaw],
  );

  const setCursorTool = useCallback<Dispatch<SetStateAction<CanvasCursorTool>>>(
    (value) => {
      setCursorToolRaw((prev) => {
        if (typeof value === "function") {
          return value(parseCanvasCursorTool(prev));
        }
        return value;
      });
    },
    [setCursorToolRaw],
  );

  const [isWidgetsMenuOpen, setIsWidgetsMenuOpen] = useState(false);
  const [isLinkMenuOpen, setIsLinkMenuOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddCursor, setQuickAddCursor] =
    useState<QuickAddCursorState | null>(null);
  const [nodeContextMenu, setNodeContextMenu] =
    useState<NodeContextMenuState | null>(null);
  const [edgeContextMenu, setEdgeContextMenu] =
    useState<EdgeContextMenuState | null>(null);
  const [isShapesMenuOpen, setIsShapesMenuOpen] = useState(false);
  const [activeConnectorPreset, setActiveConnectorPreset] =
    useState<ConnectorPreset | null>(null);
  const [isHoveringControls, setIsHoveringControls] = useState(false);
  const [isToolbarPopoverOpen, setToolbarPopoverOpen] = useState(false);
  const [isToolbarTrayDetached, setToolbarTrayDetached] = useState(false);
  const [editingEdgeId, setEditingEdgeId] = useState<string | null>(null);
  const [blockFocus, setBlockFocus] = useState<HtmlBlockFocus | null>(null);
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);
  const [reactFlowWrapperEl, setReactFlowWrapperEl] =
    useState<HTMLDivElement | null>(null);

  const isResizingRef = useRef(false);

  const value = useMemo(
    (): CanvasUiContextType => ({
      variant,
      showGrid,
      gridMode,
      setGridMode,
      cursorTool,
      setCursorTool,
      alignmentGuides,
      setAlignmentGuides,
      isWidgetsMenuOpen,
      setIsWidgetsMenuOpen,
      isLinkMenuOpen,
      setIsLinkMenuOpen,
      showQuickAdd,
      setShowQuickAdd,
      quickAddCursor,
      setQuickAddCursor,
      nodeContextMenu,
      setNodeContextMenu,
      edgeContextMenu,
      setEdgeContextMenu,
      isShapesMenuOpen,
      setIsShapesMenuOpen,
      activeConnectorPreset,
      setActiveConnectorPreset,
      isHoveringControls,
      setIsHoveringControls,
      isToolbarPopoverOpen,
      setToolbarPopoverOpen,
      isToolbarTrayDetached,
      setToolbarTrayDetached,
      editingEdgeId,
      setEditingEdgeId,
      blockFocus,
      setBlockFocus,
      reactFlowWrapperRef,
      reactFlowWrapperEl,
      setReactFlowWrapperEl,
      isResizingRef,
    }),
    [
      showGrid,
      gridMode,
      setGridMode,
      cursorTool,
      setCursorTool,
      alignmentGuides,
      setAlignmentGuides,
      isWidgetsMenuOpen,
      isLinkMenuOpen,
      showQuickAdd,
      quickAddCursor,
      nodeContextMenu,
      edgeContextMenu,
      isShapesMenuOpen,
      activeConnectorPreset,
      isHoveringControls,
      isToolbarPopoverOpen,
      isToolbarTrayDetached,
      editingEdgeId,
      blockFocus,
      reactFlowWrapperEl,
      variant,
      isResizingRef,
    ],
  );

  return (
    <CanvasUiContext.Provider value={value}>
      {children}
    </CanvasUiContext.Provider>
  );
}
